/**
 * Router Module
 * Handles client-side routing using History API
 */

class Router {
  constructor() {
    this.routes = new Map();
    this.currentRoute = null;

    // Listen for popstate (back/forward buttons)
    window.addEventListener('popstate', (e) => {
      this.handleRoute(window.location.pathname, e.state);
    });
  }

  /**
   * Register a route
   */
  on(path, handler) {
    this.routes.set(path, handler);
  }

  /**
   * Navigate to a route
   */
  navigate(path, state = {}, replace = false) {
    if (replace) {
      window.history.replaceState(state, '', path);
    } else {
      window.history.pushState(state, '', path);
    }
    this.handleRoute(path, state);
  }

  /**
   * Handle route change
   */
  handleRoute(path, state = {}) {
    console.log('Router: handleRoute called with path:', path);
    this.currentRoute = path;

    // Match exact routes first
    if (this.routes.has(path)) {
      console.log('Router: exact match found for:', path);
      this.routes.get(path)(state);
      return;
    }

    // Match dynamic routes (e.g., /wallpaper/:id)
    for (const [pattern, handler] of this.routes) {
      const params = this.matchRoute(pattern, path);
      if (params) {
        console.log('Router: dynamic route matched:', pattern, 'with params:', params);
        handler({ ...state, params });
        return;
      }
    }

    // No route matched - show 404 or default
    console.warn(`No route matched for: ${path}`);
    this.navigate('/', {}, true);
  }

  /**
   * Match a route pattern with dynamic segments
   */
  matchRoute(pattern, path) {
    const patternParts = pattern.split('/').filter(p => p);
    const pathParts = path.split('/').filter(p => p);

    if (patternParts.length !== pathParts.length) {
      return null;
    }

    const params = {};

    for (let i = 0; i < patternParts.length; i++) {
      if (patternParts[i].startsWith(':')) {
        // Dynamic segment
        const paramName = patternParts[i].slice(1);
        params[paramName] = pathParts[i];
      } else if (patternParts[i] !== pathParts[i]) {
        // Static segment doesn't match
        return null;
      }
    }

    return params;
  }

  /**
   * Initialize router with current URL
   */
  init() {
    this.handleRoute(window.location.pathname);
  }

  /**
   * Go back in history
   */
  back() {
    window.history.back();
  }
}

export default new Router();
